import { useState, useEffect, useCallback, useRef } from 'react';
import aiService from '../services/aiService';
import { useMode } from '../context/ModeContext';
import { useAuth } from '../context/AuthContext';

/**
 * useLinaProactive — Custom hook for managing Lina's proactive notifications.
 *
 * Responsibilities:
 *  - Listen for real-time `lina:proactive` events via SocketContext (window events)
 *  - Poll for pending notifications on mount
 *  - Manage notification state (add, mark read, dismiss)
 *  - Provide preference management APIs
 *
 * @returns {Object}
 *   notifications     — Array of proactive notification objects
 *   unreadCount       — Number of unread notifications
 *   markRead          — (eventId) => void
 *   dismiss           — (eventId) => void
 *   dismissAll        — () => void
 *   refreshNotifications — () => void
 *   preferences       — User's proactive preferences (or null)
 *   updatePreferences — (prefs) => void
 *   isLoading         — Boolean
 */
export default function useLinaProactive() {
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);
  const { currentMode } = useMode();
  const { currentUser } = useAuth();

  const workspace = currentMode === 'organization' ? 'organization' : 'personal';
  const organizationId = currentMode === 'organization' ? (currentUser?.organizationId || null) : null;

  // ── Fetch pending notifications on mount ─────────────────────────────────
  const refreshNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const events = await aiService.getProactiveNotifications(10, workspace, organizationId);
      if (mountedRef.current) {
        setNotifications(events);
      }
    } catch (err) {
      console.error('[useLinaProactive] Failed to fetch notifications:', err);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [workspace, organizationId]);

  // ── Listen for real-time proactive events ────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;

    // Initial fetch
    refreshNotifications();

    // Real-time listener — SocketContext dispatches `lina:proactive` window events
    const handleProactive = (e) => {
      const event = e.detail;
      if (!event || !event.eventId) return;

      setNotifications((prev) => {
        const existingIdx = prev.findIndex(
          (n) => (n._id || n.eventId) === event.eventId || (event.isUpdate && n.triggerId === event.triggerId)
        );

        const normalized = {
          _id: event.eventId,
          triggerId: event.triggerId,
          triggerType: event.triggerType,
          title: event.title,
          message: event.message,
          sections: event.sections || [],
          suggestedActions: event.suggestedActions || [],
          urgency: event.urgency || 'medium',
          status: 'delivered',
          createdAt: event.timestamp || new Date().toISOString(),
        };

        // If this is an update or a duplicate, replace the existing one in-place
        if (existingIdx !== -1) {
          const updated = [...prev];
          updated[existingIdx] = normalized;
          return updated;
        }

        // New notification — add to top
        return [normalized, ...prev].slice(0, 20);
      });
    };

    window.addEventListener('lina:proactive', handleProactive);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('lina:proactive', handleProactive);
    };
  }, [refreshNotifications]);

  // ── Mark notification as read ────────────────────────────────────────────
  const markRead = useCallback(async (eventId) => {
    setNotifications((prev) =>
      prev.map((n) => (n._id === eventId ? { ...n, status: 'read', readAt: new Date().toISOString() } : n))
    );
    try {
      await aiService.markProactiveRead(eventId);
    } catch (err) {
      console.error('[useLinaProactive] Failed to mark read:', err);
    }
  }, []);

  // ── Dismiss notification ─────────────────────────────────────────────────
  const dismiss = useCallback(async (eventId) => {
    setNotifications((prev) => prev.filter((n) => n._id !== eventId));
    try {
      await aiService.dismissProactive(eventId);
    } catch (err) {
      console.error('[useLinaProactive] Failed to dismiss:', err);
    }
  }, []);

  // ── Dismiss all ──────────────────────────────────────────────────────────
  const dismissAll = useCallback(async () => {
    const ids = notifications.map((n) => n._id);
    setNotifications([]);
    for (const id of ids) {
      try {
        await aiService.dismissProactive(id);
      } catch {
        // Silently continue
      }
    }
  }, [notifications]);

  // ── Preferences ──────────────────────────────────────────────────────────
  const fetchPreferences = useCallback(async () => {
    try {
      const data = await aiService.getProactivePreferences();
      if (mountedRef.current) setPreferences(data);
    } catch (err) {
      console.error('[useLinaProactive] Failed to fetch preferences:', err);
    }
  }, []);

  const updatePreferences = useCallback(async (prefs) => {
    try {
      const updated = await aiService.updateProactivePreferences(prefs);
      if (mountedRef.current) {
        setPreferences((prev) => ({
          ...prev,
          preferences: updated,
        }));
      }
    } catch (err) {
      console.error('[useLinaProactive] Failed to update preferences:', err);
    }
  }, []);

  // ── Computed ─────────────────────────────────────────────────────────────
  const unreadCount = notifications.filter(
    (n) => n.status === 'pending' || n.status === 'delivered'
  ).length;

  return {
    notifications,
    unreadCount,
    markRead,
    dismiss,
    dismissAll,
    refreshNotifications,
    preferences,
    fetchPreferences,
    updatePreferences,
    isLoading,
  };
}
